import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import FuseSplashScreen from '@fuse/core/FuseSplashScreen';
import { getUser, getUserLogo, resetUserState } from 'app/store/userSlice';
import { setInitialSettings } from 'app/store/fuse/settingsSlice';
import { refreshToken } from 'app/store/authSlice';

const AuthContext = React.createContext({
  waitAuthCheck: true,
  isAuthenticated: false,
  didCheckAuth: false,
});

let didInit = false;

function AuthProvider({ children }) {
  const [waitAuthCheck, setWaitAuthCheck] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [didCheckAuth, setDidCheckAuth] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const checkAuthStatus = async () => {
      const isNewTokenValid = async () => {
        const token = await dispatch(refreshToken());
        setDidCheckAuth(true);
        return token?.payload;
      };

      if (!didInit) {
        didInit = true;
        const isValid = await isNewTokenValid();

        if (isValid) {
          const userData = await dispatch(getUser());
          if (userData.meta.requestStatus === 'fulfilled') {
            setIsAuthenticated(true);
            setWaitAuthCheck(false);
            await dispatch(getUserLogo(userData.payload.id)).unwrap();
          } else {
            dispatch(resetUserState());
            dispatch(setInitialSettings());
            setWaitAuthCheck(false);
          }
        } else {
          setIsAuthenticated(false);
          setWaitAuthCheck(false);
        }
      }
    };

    checkAuthStatus();
  }, []);

  const providerValue = useMemo(() => {
    return { isAuthenticated, waitAuthCheck, didCheckAuth };
  }, [isAuthenticated, waitAuthCheck, didCheckAuth]);
  return waitAuthCheck ? (
    <FuseSplashScreen />
  ) : (
    <AuthContext.Provider value={providerValue}>{children}</AuthContext.Provider>
  );
}

function useAuth() {
  const context = React.useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}

export { AuthProvider, useAuth, AuthContext };
